/**
 * Keeps every in-memory record pointing at the right registry person, so all
 * calculations share one key per person. See docs/resources-unique-ids-spec.md.
 *
 * An ID is trusted only while its registry entry still has the record's name.
 * Otherwise the record is matched by name, which repairs IDs that are missing,
 * out of date, or misaligned after an edit that changed only the names.
 *
 * Every function returns the same reference when nothing changes, so running
 * these after each store update can't cause extra renders or update loops.
 */

import { normalizeResourceName, resourceKeyFor } from '@/lib/resources';
import type {
  Project,
  Resource,
  ResourceRoleAssignment,
  ResourceSkillLevel,
  ResourceTimeOff,
  ScenarioOverride,
  Task,
  TeamMember,
} from '@/types';

export interface RegistryIndex {
  byId: Map<string, Resource>;
  byName: Map<string, Resource>;
}

export function indexRegistry(resources: Resource[]): RegistryIndex {
  const byId = new Map<string, Resource>();
  const byName = new Map<string, Resource>();
  for (const r of resources) {
    byId.set(r.id, r);
    if (!byName.has(r.nameNormalized)) byName.set(r.nameNormalized, r);
  }
  return { byId, byName };
}

/** The registry ID for this name, or null if the person isn't in the registry. */
export function resolveResourceId(
  index: RegistryIndex,
  id: string | null | undefined,
  name: string | null | undefined,
): string | null {
  const key = normalizeResourceName(name ?? '');
  if (!key) return null;
  const row = id ? index.byId.get(id) : undefined;
  if (row && row.nameNormalized === key) return row.id;
  return index.byName.get(key)?.id ?? null;
}

/** The key a name resolves to right now: the registry ID, or the name if unregistered. */
export function resourceKeyForName(index: RegistryIndex, name: string): string {
  return resourceKeyFor(resolveResourceId(index, null, name), name);
}

export function canonicalizeTask(task: Task, index: RegistryIndex): Task {
  const resources = task.resources ?? [];
  const current = task.resourceIds;
  if (resources.length === 0 && !current?.length) return task;
  const next = resources.map((name, i) => resolveResourceId(index, current?.[i], name));
  const unchanged =
    current?.length === next.length && next.every((id, i) => (current[i] ?? null) === id);
  return unchanged ? task : { ...task, resourceIds: next };
}

function canonicalizeMember(member: TeamMember, index: RegistryIndex): TeamMember {
  const id = resolveResourceId(index, member.resourceId, member.name);
  if ((member.resourceId ?? null) === id) return member;
  const { resourceId: _stale, ...rest } = member;
  return id ? { ...rest, resourceId: id } : rest;
}

function mapIfChanged<T>(items: T[], fn: (item: T) => T): T[] {
  let changed = false;
  const next = items.map((item) => {
    const mapped = fn(item);
    if (mapped !== item) changed = true;
    return mapped;
  });
  return changed ? next : items;
}

export function canonicalizeTasks(tasks: Task[], index: RegistryIndex): Task[] {
  return mapIfChanged(tasks, (t) => canonicalizeTask(t, index));
}

export function canonicalizeTeam(team: TeamMember[], index: RegistryIndex): TeamMember[] {
  return mapIfChanged(team, (m) => canonicalizeMember(m, index));
}

export function canonicalizeProjects(projects: Project[], index: RegistryIndex): Project[] {
  return mapIfChanged(projects, (p) => {
    const team = p.team ? canonicalizeTeam(p.team, index) : p.team;
    const wbsTasks = p.wbsTasks ? canonicalizeTasks(p.wbsTasks, index) : p.wbsTasks;
    return team === p.team && wbsTasks === p.wbsTasks ? p : { ...p, team, wbsTasks };
  });
}

export function canonicalizeTimeOff(entries: ResourceTimeOff[], index: RegistryIndex): ResourceTimeOff[] {
  return mapIfChanged(entries, (entry) => {
    const id = resolveResourceId(index, entry.resourceId, entry.nameNormalized || entry.displayName);
    if ((entry.resourceId ?? null) === id) return entry;
    const { resourceId: _stale, ...rest } = entry;
    return id ? { ...rest, resourceId: id } : rest;
  });
}

export function canonicalizeOverrides(
  overrides: ScenarioOverride[],
  index: RegistryIndex,
): ScenarioOverride[] {
  return mapIfChanged(overrides, (override) => {
    const id = resolveResourceId(index, override.resourceId, override.resourceName);
    if ((override.resourceId ?? null) === id) return override;
    const { resourceId: _stale, ...rest } = override;
    return id ? { ...rest, resourceId: id } : rest;
  });
}

export function canonicalizeRoleAssignments(
  assignments: ResourceRoleAssignment[],
  index: RegistryIndex,
): ResourceRoleAssignment[] {
  return mapIfChanged(assignments, (a) => {
    const id = resolveResourceId(index, a.resourceId, a.resourceName);
    if ((a.resourceId ?? null) === id) return a;
    const { resourceId: _stale, ...rest } = a;
    return id ? { ...rest, resourceId: id } : rest;
  });
}

export function canonicalizeSkillLevels(
  levels: ResourceSkillLevel[],
  index: RegistryIndex,
): ResourceSkillLevel[] {
  return mapIfChanged(levels, (l) => {
    const id = resolveResourceId(index, l.resourceId, l.nameNormalized);
    if ((l.resourceId ?? null) === id) return l;
    const { resourceId: _stale, ...rest } = l;
    return id ? { ...rest, resourceId: id } : rest;
  });
}

/** Capacity entries remember the name they're stored under in the database. */
export interface CapacityEntryBase {
  hoursPerWeek: number;
  nameKey?: string;
}

/** Keys capacity by person. An entry's key is the registry ID, or its name if unregistered. */
export function canonicalizeCapacities<T extends CapacityEntryBase>(
  capacities: Record<string, T>,
  index: RegistryIndex,
): Record<string, T> {
  let changed = false;
  const next: Record<string, T> = {};
  for (const [key, entry] of Object.entries(capacities)) {
    const nameKey = entry.nameKey ?? key;
    const row = index.byId.get(key) ?? index.byName.get(nameKey);
    const nextKey = row?.id ?? nameKey;
    const nextNameKey = row?.nameNormalized ?? nameKey;
    if (nextKey !== key || entry.nameKey !== nextNameKey) changed = true;
    next[nextKey] = entry.nameKey === nextNameKey ? entry : { ...entry, nameKey: nextNameKey };
  }
  return changed ? next : capacities;
}
